// Servicio para manejar autenticación con PetFlix
import { environment } from '../config/environment';

class PetflixAuthService {
  constructor() {
    this.baseUrl = environment.petflixApiUrl;
    //this.testing = true; // Variable para controlar si se imprime OTP en terminal o se envía por email
    //this.testing = false;
    //this.testing = true;
    this.testing = false;
  }

  // Paso 3: Autenticar con token de Firebase en PetFlix
  async authenticateWithFirebase(idToken) {
    try {
      const response = await fetch(`${this.baseUrl}/authentication`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          strategy: 'firebase',
          access_token: idToken
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Guardar información del usuario si está disponible
      if (data.user) {
        try {
          localStorage.setItem('petflix_user_info', JSON.stringify(data.user));
        } catch (e) {
          console.warn('No se pudo guardar información del usuario:', e);
        }
      }
      
      return {
        success: true,
        data: {
          accessToken: data.accessToken,
          user: data.user,
          authentication: data.authentication
        }
      };
    } catch (error) {
      console.error('PetFlix authentication error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Obtener OTP para verificación
  async requestOTP(email, firstName, lastName) {
    try {
      if (this.testing) {
        // Modo Testing: Generar OTP local y mostrarlo en consola
        const otp = Math.floor(100000 + Math.random() * 900000).toString(); // Generar OTP de 6 dígitos
        console.log('🔐 OTP GENERADO PARA TESTING:');
        console.log('📧 Email:', email);
        if (firstName && lastName) {
          console.log('👤 Nombre:', firstName, lastName);
        }
        console.log('🔑 Código OTP:', otp);
        console.log('⏰ Válido por 10 minutos');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        
        // Almacenar OTP en localStorage para mostrarlo en la interfaz
        try {
          localStorage.setItem('petflix_testing_otp', otp);
          localStorage.setItem('petflix_testing_otp_email', email);
        } catch (e) {
          console.warn('No se pudo almacenar OTP en localStorage:', e);
        }
        
        return {
          success: true,
          data: {
            otp: otp,
            email: email,
            message: 'OTP generado para testing (ver consola)',
            testing: true
          }
        };
      } else {
        // Modo Producción: Enviar OTP por email
        const requestBody = {
          email: email
        };
        
        // Agregar first_name y last_name si están disponibles
        if (firstName && lastName) {
          requestBody.first_name = firstName;
          requestBody.last_name = lastName;
        }
        
        const response = await fetch(`${this.baseUrl}/authentication/email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        
        // Mostrar la respuesta completa de la API para debug
        console.log('📋 Respuesta completa de la API:', JSON.stringify(data, null, 2));
        
        // También mostrar el OTP en consola si viene en la respuesta (útil para desarrollo)
        if (data.otp) {
          console.log('🔐 OTP GENERADO (Modo Producción):');
          console.log('📧 Email:', email);
          console.log('🔑 Código OTP:', data.otp);
          console.log('⏰ Válido por 10 minutos');
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          
          // También guardarlo en localStorage para mostrarlo en la interfaz si es necesario
          try {
            localStorage.setItem('petflix_production_otp', data.otp);
            localStorage.setItem('petflix_production_otp_email', email);
          } catch (e) {
            console.warn('No se pudo almacenar OTP en localStorage:', e);
          }
        } else {
          console.log('📧 OTP enviado por email a:', email);
          console.log('💡 Revisa tu bandeja de entrada para obtener el código');
          console.log('⚠️  Nota: La API no devuelve el OTP en la respuesta por seguridad.');
          console.log('   El código solo está disponible en el email enviado.');
          
          // Mostrar todas las claves de la respuesta para ver qué devuelve
          if (data && typeof data === 'object') {
            console.log('📋 Claves en la respuesta:', Object.keys(data).join(', '));
          }
        }
        
        return {
          success: true,
          data: {
            ...data,
            message: 'OTP enviado por email',
            testing: false
          }
        };
      }
    } catch (error) {
      console.error('OTP request error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Obtener OTP para verificación por teléfono
  async requestPhoneOTP(phoneNumber) {
    try {
      console.log('📱 Solicitando OTP por teléfono:', phoneNumber);
      
      const response = await fetch(`${this.baseUrl}/authentication/phone-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: phoneNumber
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📋 Respuesta de phone-otp:', JSON.stringify(data, null, 2));
      
      return {
        success: true,
        data: {
          ...data,
          phone: phoneNumber,
          message: 'OTP enviado por SMS'
        }
      };
    } catch (error) {
      console.error('Phone OTP request error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Verificar OTP por teléfono
  async verifyPhoneOTP(phoneNumber, otp) {
    try {
      console.log('🔍 VERIFICANDO OTP POR TELÉFONO:');
      console.log('📱 Teléfono:', phoneNumber);
      console.log('🔑 Código OTP ingresado:', otp);
      
      // Codificar los parámetros para la URL
      const encodedPhone = encodeURIComponent(phoneNumber);
      const encodedCode = encodeURIComponent(otp);
      
      const response = await fetch(`${this.baseUrl}/authentication/phone-otp?phone=${encodedPhone}&code=${encodedCode}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.log('❌ Error en verificación de OTP por teléfono:', errorData.message || `Error ${response.status}`);
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const accessToken = data.accessToken;
      
      console.log('✅ OTP por teléfono verificado exitosamente');
      console.log('🎫 Access Token obtenido (completo):');
      console.log(accessToken);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      
      // Guardar el token automáticamente
      this.setAccessToken(accessToken);
      console.log('✅ Access Token guardado en localStorage');
      
      // Guardar información del usuario en localStorage
      if (data.user) {
        try {
          localStorage.setItem('petflix_user_info', JSON.stringify(data.user));
          console.log('✅ Información del usuario guardada');
        } catch (e) {
          console.warn('No se pudo guardar información del usuario:', e);
        }
      }
      
      return {
        success: true,
        data: {
          accessToken: accessToken,
          user: data.user,
          authentication: data.authentication,
          phone: phoneNumber,
          testing: false
        }
      };
    } catch (error) {
      console.error('Phone OTP verification error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Verificar OTP
  async verifyOTP(email, otp) {
    try {
      if (this.testing) {
        // Modo Testing: Verificación local simple
        console.log('🔍 VERIFICANDO OTP EN MODO TESTING:');
        console.log('📧 Email:', email);
        console.log('🔑 Código ingresado:', otp);
        
        // Simular verificación exitosa (en testing siempre es válido)
        if (otp && otp.length === 6) {
          console.log('✅ OTP VÁLIDO - Acceso autorizado');
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          
          return {
            success: true,
            data: {
              message: 'OTP verificado correctamente (modo testing)',
              email: email,
              testing: true
            }
          };
        } else {
          console.log('❌ OTP INVÁLIDO - Código debe tener 6 dígitos');
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          
          return {
            success: false,
            error: 'Código OTP inválido (debe tener 6 dígitos)'
          };
        }
      } else {
        // Modo Producción: El OTP se usa como password en Firebase
        console.log('🔍 VERIFICANDO OTP EN MODO PRODUCCIÓN:');
        console.log('📧 Email:', email);
        console.log('🔑 Código OTP ingresado:', otp);
        
        // Paso 1: Login con Firebase usando el OTP como password
        console.log('\n🔐 Paso 1: Autenticando con Firebase usando OTP como password...');
        const firebaseApiKey = 'AIzaSyCZtlAJo6dxv4MW6ep_xV0VTBfDq0VJEFo';
        const firebaseResponse = await fetch(
          `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${firebaseApiKey}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              returnSecureToken: true,
              email: email,
              password: otp, // El OTP se usa como password
              clientType: 'CLIENT_TYPE_WEB'
            })
          }
        );

        if (!firebaseResponse.ok) {
          const firebaseError = await firebaseResponse.json().catch(() => ({}));
          console.log('❌ Error en Firebase:', firebaseError.error?.message || `Error ${firebaseResponse.status}`);
          throw new Error(firebaseError.error?.message || `Error en Firebase: ${firebaseResponse.status}`);
        }

        const firebaseData = await firebaseResponse.json();
        const idToken = firebaseData.idToken;
        console.log('✅ Firebase autenticación exitosa');
        console.log('📧 Email verificado:', firebaseData.email);

        // Paso 2: Lookup del usuario (opcional, para verificar)
        console.log('\n🔐 Paso 2: Verificando usuario en Firebase...');
        const lookupResponse = await fetch(
          `https://identitytoolkit.googleapis.com/v1/accounts:lookup?key=${firebaseApiKey}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              idToken: idToken
            })
          }
        );

        if (lookupResponse.ok) {
          const lookupData = await lookupResponse.json();
          console.log('✅ Usuario verificado en Firebase');
        }

        // Paso 3: Autenticar con Petflix usando el token de Firebase
        console.log('\n🔐 Paso 3: Autenticando con Petflix...');
        const petflixResponse = await fetch(`${this.baseUrl}/authentication`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            strategy: 'firebase',
            access_token: idToken
          })
        });

        if (!petflixResponse.ok) {
          const petflixError = await petflixResponse.json().catch(() => ({}));
          console.log('❌ Error en Petflix:', petflixError.message || `Error ${petflixResponse.status}`);
          throw new Error(petflixError.message || `Error en Petflix: ${petflixResponse.status}`);
        }

        const petflixData = await petflixResponse.json();
        const accessToken = petflixData.accessToken;
        
        console.log('✅ Petflix autenticación exitosa');
        console.log('🎫 Access Token obtenido (completo):');
        console.log(accessToken);
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        
        // Guardar el token automáticamente
        this.setAccessToken(accessToken);
        console.log('✅ Access Token guardado en localStorage');
        
        // Guardar información del usuario en localStorage
        if (petflixData.user) {
          try {
            localStorage.setItem('petflix_user_info', JSON.stringify(petflixData.user));
            console.log('✅ Información del usuario guardada');
          } catch (e) {
            console.warn('No se pudo guardar información del usuario:', e);
          }
        }
        
        // Intentar guardarlo también en la configuración
        try {
          const { setPetflixAccessToken } = await import('../config/petflixAuth');
          setPetflixAccessToken(accessToken);
          console.log('✅ Access Token guardado en configuración');
        } catch (e) {
          // Si no se puede importar, solo está en localStorage
          console.log('ℹ️  Token guardado solo en localStorage');
        }
        
        return {
          success: true,
          data: {
            accessToken: accessToken,
            user: petflixData.user,
            authentication: petflixData.authentication,
            email: email,
            testing: false
          }
        };
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Almacenar token en localStorage
  setAccessToken(token) {
    localStorage.setItem('petflix_access_token', token);
  }

  // Obtener token del localStorage
  getAccessToken() {
    return localStorage.getItem('petflix_access_token');
  }

  // Obtener información del usuario del localStorage
  getUserInfo() {
    try {
      const userInfoStr = localStorage.getItem('petflix_user_info');
      if (userInfoStr) {
        return JSON.parse(userInfoStr);
      }
      return null;
    } catch (e) {
      console.error('Error obteniendo información del usuario:', e);
      return null;
    }
  }

  // Guardar información del usuario en localStorage
  setUserInfo(userInfo) {
    try {
      localStorage.setItem('petflix_user_info', JSON.stringify(userInfo));
    } catch (e) {
      console.error('Error guardando información del usuario:', e);
    }
  }

  // Limpiar token del localStorage
  clearAccessToken() {
    localStorage.removeItem('petflix_access_token');
    localStorage.removeItem('petflix_user_info');
  }

  // Verificar si hay token válido
  isAuthenticated() {
    const token = this.getAccessToken();
    return token && token.length > 0;
  }

  // Cambiar modo testing
  setTestingMode(enabled) {
    this.testing = enabled;
    console.log(`🧪 Modo Testing ${enabled ? 'ACTIVADO' : 'DESACTIVADO'}`);
    console.log(`📧 OTP se ${enabled ? 'imprimirá en consola' : 'enviará por email'}`);
  }

  // Obtener estado del modo testing
  isTestingMode() {
    return this.testing;
  }
}

export default new PetflixAuthService();
